import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { trackError, ErrorSeverity } from './error-tracking';
import { captureScrollAnchor, holdScrollAnchor, isScrollAnchor, type ScrollAnchor } from './scroll-anchor';
import { readJSON, removeKey, writeJSON } from './guarded-storage';
import { reportBackRestore, watchMainThread, type BackRestoreWait } from './back-restore-report';
import { errorText } from './error-message';

interface PaginationState {
  page: number;
  done: boolean;
}

interface UseScrollRestorationOptions {
  page?: number;
  setPage?: (page: number) => void;
  done?: boolean;
  /** Optional: a page whose end-of-list flag is derived, not stored, has nothing to restore. */
  setDone?: (done: boolean) => void;
}

interface UseScrollRestorationReturn {
  isBackNavigation: boolean;
}

/**
 * Custom hook to preserve scroll position AND pagination state when navigating away and back to listing pages
 *
 * This solves the issue where:
 * 1. User scrolls down a listing page with infinite scroll
 * 2. User clicks on a product
 * 3. User presses back
 * 4. User expects to be at the same scroll position with all loaded pages, not at the top with only first page
 *
 * Usage:
 * ```
 * const MyListingPage = () => {
 *   const [page, setPage] = useState(1);
 *   const [done, setDone] = useState(false);
 *   useScrollRestoration({ page, setPage, done, setDone });
 *   return <div>...</div>;
 * };
 * ```
 */
export const useScrollRestoration = (options?: UseScrollRestorationOptions): UseScrollRestorationReturn => {
  const router = useRouter();
  const scrollPositions = useRef<{ [url: string]: number }>({});
  const isRestoringRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const [isBackNavigation, setIsBackNavigation] = useState(false);

  // Destructure options for stable dependency references
  const optionsPage = options?.page;
  const optionsSetPage = options?.setPage;
  const optionsDone = options?.done;
  const optionsSetDone = options?.setDone;

  // Track back/forward navigation
  useEffect(() => {
    // Detect browser back/forward button navigation
    router.beforePopState(() => {
      setIsBackNavigation(true);
      return true;
    });

    // Reset flag after navigation completes
    const handleComplete = () => {
      // Delay reset to ensure component has time to read the flag
      setTimeout(() => setIsBackNavigation(false), 100);
    };

    router.events.on('routeChangeComplete', handleComplete);

    return () => {
      router.events.off('routeChangeComplete', handleComplete);
    };
  }, [router]);

  useEffect(() => {
    // Every other storage access in this hook is guarded, because reading
    // sessionStorage throws outright in Safari private mode and in some
    // embedded webviews. These two checks were not, so the hook threw during
    // mount and took the whole listing page with it.
    const savedScrollFor = (url: string): string | null => {
      try {
        return sessionStorage.getItem(`scroll_${url}`);
      } catch (_e) {
        return null;
      }
    };

    // Function to save current scroll position
    const saveScrollPosition = (url: string) => {
      const scrollY = window.scrollY || window.pageYOffset;
      scrollPositions.current[url] = scrollY;
      // And the listing at the top of the screen, which Back holds in place
      // while the page settles (utils/scroll-anchor.ts).
      const anchor = captureScrollAnchor();
      if (anchor) writeJSON('session', `scrollanchor_${url}`, anchor);
      else removeKey('session', `scrollanchor_${url}`);

      // Also save to sessionStorage for persistence across potential remounts
      try {
        sessionStorage.setItem(`scroll_${url}`, String(scrollY));
      } catch (e) {
        // Ignore errors (e.g., in private browsing mode)
        trackError('Failed to save scroll position to sessionStorage', ErrorSeverity.WARNING, {
          component: 'useScrollRestoration',
          action: 'saveScrollPosition',
          extra: { error: errorText(e) },
        });
      }
    };

    // Function to save pagination state
    const savePaginationState = (url: string) => {
      if (!optionsPage || optionsDone === undefined) return;

      const paginationState: PaginationState = {
        page: optionsPage,
        done: optionsDone,
      };

      try {
        sessionStorage.setItem(`pagination_${url}`, JSON.stringify(paginationState));
      } catch (e) {
        trackError('Failed to save pagination state to sessionStorage', ErrorSeverity.WARNING, {
          component: 'useScrollRestoration',
          action: 'savePaginationState',
          extra: { error: errorText(e) },
        });
      }
    };

    // Function to restore pagination state
    const restorePaginationState = (url: string) => {
      if (!optionsSetPage) return;

      try {
        const stored = sessionStorage.getItem(`pagination_${url}`);
        if (stored) {
          const paginationState: PaginationState = JSON.parse(stored);
          optionsSetPage(paginationState.page);
          optionsSetDone?.(paginationState.done);
        }
      } catch (e) {
        trackError('Failed to restore pagination state from sessionStorage', ErrorSeverity.WARNING, {
          component: 'useScrollRestoration',
          action: 'restorePaginationState',
          extra: { error: errorText(e) },
        });
      }
    };

    // Where the reader left `url`: this mount's own record first, then
    // sessionStorage (a remount). Null when nothing was saved.
    const savedPositionFor = (url: string): number | null => {
      const inRef = scrollPositions.current[url];
      if (inRef !== undefined) return inRef;
      const stored = savedScrollFor(url);
      return stored ? parseInt(stored, 10) : null;
    };

    // Function to restore scroll position - waits for content to be rendered
    const restoreScrollPosition = (url: string, savedPosition: number) => {
      if (savedPosition > 0) {
        isRestoringRef.current = true;

        // The pages the reader had loaded, for the Back report.
        let savedPages = 0;
        try {
          const paginationStored = sessionStorage.getItem(`pagination_${url}`);
          if (paginationStored) {
            const pagination: PaginationState = JSON.parse(paginationStored);
            savedPages = Number(pagination.page) || 0;
          }
        } catch (_e) {
          // Ignore
        }

        // Wait for content to be rendered before scrolling. `wait` is for the
        // Back report: when the waiting began, and what it was last waiting on.
        // `firstTickAt` and `mainThread` say why the first look came late, when it does.
        const wait: BackRestoreWait = { startedAt: performance.now(), waitedOn: 'none', mainThread: watchMainThread() };
        setTimeout(() => {
          wait.firstTickAt = performance.now();
        }, 0);
        const attemptScroll = (attempt = 0) => {
          const maxAttempts = 100; // Try for up to 5 seconds (increased from 1.5s)

          // Check if listing grid exists and has items
          const listingGrid = document.querySelector('[data-testid="listing-grid"], .listing-cont, [data-testid="showcase-view"]');
          const listingItems = listingGrid ? listingGrid.querySelectorAll('.listing, .showcase') : [];

          // Get document height
          const documentHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );

          // The page as this check saw it, for the Back report.
          wait.page = {
            firstLookAt: wait.page?.firstLookAt ?? performance.now(),
            hidden: (wait.page?.hidden ?? false) || document.visibilityState === 'hidden',
            tiles: listingItems.length,
            holdTiles: document.querySelectorAll('.listing-image-cont.skeleton').length,
            savedPages,
            shortPx: savedPosition + window.innerHeight * 0.5 - documentHeight,
          };

          // Back is ready once there are tiles and the page is tall enough to
          // scroll to where the reader was. It used to wait for a tile count
          // too (saved pages x 32), from before the grid held its height with
          // skeletons (components/search-listing/useGridHoldOnBack.ts) and Back
          // held the reader's listing in place (utils/scroll-anchor.ts). The
          // count only ever overshot: load-ahead counts pages the reader never
          // scrolled to, For You drops repeats, a search ends short. Prod
          // 2026-10-28: all 5 of 21 Backs that gave up and dropped the reader
          // at the top (For You, search, browse) were waiting on it, every one
          // with the page already tall enough.
          const hasEnoughContent = documentHeight >= savedPosition + window.innerHeight * 0.5;
          const hasListings = listingItems.length > 0;

          if (hasEnoughContent && hasListings) {
            // Content is ready, scroll now
            window.scrollTo(0, savedPosition);
            // The banner ad and tag pills above the grid can still load and
            // push the tiles down; hold the listing the reader left at the top
            // where it was.
            const anchor = readJSON<ScrollAnchor | null>('session', `scrollanchor_${url}`, null, isScrollAnchor);
            if (anchor) holdScrollAnchor(anchor);
            // How long Back took and where it landed, for frontend-health.
            reportBackRestore({ outcome: 'restored', savedY: savedPosition, anchor, wait });

            // Reset flag after scrolling
            setTimeout(() => {
              isRestoringRef.current = false;
            }, 100);
          } else if (attempt >= maxAttempts) {
            // Gave up waiting - scroll to top instead of wrong position
            window.scrollTo(0, 0);
            reportBackRestore({ outcome: 'gave_up', savedY: savedPosition, anchor: null, wait });
            isRestoringRef.current = false;
          } else {
            wait.waitedOn = !hasListings ? 'listings' : 'height';
            // Content not ready yet, try again after a short delay
            setTimeout(() => attemptScroll(attempt + 1), 50);
          }
        };

        // Start attempting to scroll after initial render
        requestAnimationFrame(() => attemptScroll());
      }
    };

    // Handler for route change start - save current position and pagination state
    const handleRouteChangeStart = (_url: string) => {
      // Only save if we're not in the middle of a restore operation
      if (!isRestoringRef.current) {
        saveScrollPosition(router.asPath);
        savePaginationState(router.asPath);
      }
    };

    // Handler for route change complete - restore pagination state and scroll position
    const handleRouteChangeComplete = (url: string) => {
      // Don't restore scroll for home page on forward navigation
      // (only restore on back navigation)
      if (url === '/' && !isBackNavigation) {
        // Clear any saved scroll for home page on forward navigation
        delete scrollPositions.current['/'];
        try {
          sessionStorage.removeItem('scroll_/');
          sessionStorage.removeItem('pagination_/');
        } catch (_e) {
          // Ignore
        }
        window.scrollTo(0, 0);
        return;
      }

      // Check if this is a back navigation with saved scroll position
      const savedPosition = savedPositionFor(url);

      if (isBackNavigation && savedPosition !== null) {
        // Back navigation - restore scroll position
        restorePaginationState(url);
        restoreScrollPosition(url, savedPosition);
      } else {
        // Forward navigation or new page - scroll to top
        window.scrollTo(0, 0);
      }
    };

    // Subscribe to router events
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    // Save scroll position and pagination state before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition(router.asPath);
      savePaginationState(router.asPath);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Try to restore pagination state and scroll position on initial mount
    // This handles the case where user navigates back using browser back button
    // Only do this once on mount
    if (!hasInitializedRef.current) {
      const currentUrl = router.asPath;
      const savedPosition = savedPositionFor(currentUrl);
      if (savedPosition !== null) {
        // Restore pagination state first, then scroll position
        restorePaginationState(currentUrl);
        restoreScrollPosition(currentUrl, savedPosition);
      }
      hasInitializedRef.current = true;
    }

    // Cleanup
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [router, optionsPage, optionsSetPage, optionsDone, optionsSetDone, isBackNavigation]);

  return { isBackNavigation };
};
